import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

// ✅ Token Headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ Type: LeadSource
export interface LeadSource {
  LeadSource_id?: string;
  LeadSource_name: string;
  Lead_status: string;
  created_at?: string;
  client?: {
    client_id: string;
  };
}

// ✅ CREATE Lead Source
export const createLeadSource = async (
  data: { LeadSource_name: string; Lead_status?: string }
): Promise<LeadSource | null> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadSource_name", data.LeadSource_name);
    form.append("Lead_status", data.Lead_status || "Active");

    const res = await axios.post(`${API_BASE_PATH}/lead_source_create/`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Lead Source created successfully!");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Lead Source";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ GET ALL Lead Sources
export const fetchAllLeadSources = async (): Promise<LeadSource[]> => {
  try {
    const res = await axios.get(`${API_BASE_PATH}/leadSource/getall/table/data`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Sources";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ GET ALL Lead Sources
export const fetchAllLeadSourcesActive = async (): Promise<LeadSource[]> => {
  try {
    const res = await axios.get(`${API_BASE_PATH}/leadSource/getall/table/data/active`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Sources";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ UPDATE Lead Source
export const updateLeadSource = async (
  leadsource_id: string,
  data: { LeadSource_name: string; Lead_status?: string }
): Promise<boolean> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadSource_name", data.LeadSource_name);
    form.append("Lead_status", data.Lead_status || "Active");

    await axios.put(`${API_BASE_PATH}/leadsource/${leadsource_id}/update`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Lead Source updated successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update Lead Source11";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ DELETE Lead Source
export const deleteLeadSource = async (leadsource_id: string): Promise<boolean> => {
  try {
    await axios.delete(`${API_BASE_PATH}/leadsource/${leadsource_id}/delete`, {
      headers: getAuthHeaders(),
    });

    toast.success("Lead Source deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete Lead Source";
    toast.error(errorMessage);
    return false;
  }
};

/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Lead Sources – Excel
export const exportLeadSourceExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_source_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead sources exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead sources (Excel)");
    throw error;
  }
};

// ✅ Export all Lead Sources – PDF
export const exportLeadSourcePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_source_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead sources exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead sources (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Lead Sources – Excel
export const exportSelectedLeadSourceExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_source_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead sources exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead sources (Excel)");
    throw error;
  }
};

// ✅ Export selected Lead Sources – PDF
export const exportSelectedLeadSourcePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_source_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead sources exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead sources (PDF)");
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Lead Sources
export const deleteLeadSourceBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    await axios.delete(
      `${API_BASE_PATH}/lead_source_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    //toast.success("Lead sources deleted successfully");
  } catch (error) {
    if ((error as any)?.response?.status === 403) {
      console.error("Unauthorized to delete lead sources");
    } else {
      handleAxiosError(error, "Failed to delete lead sources");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface LeadSourceImportResult {
  message: string;
  added: {
    lead_source_name: string;
    lead_source_status: string;
  }[];
  skipped: {
    row: number;
    lead_source_name: string;
    reason: string;
  }[];
}


// ✅ Import Lead Source Excel
export const importLeadSourceExcel = async (
  file: File
): Promise<LeadSourceImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/lead_source_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Lead source import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import lead sources");
    throw error;
  }
};